import Backbone from 'backbone'
import _ from 'lodash'
import constants from 'scoreboard/components/constants'
import nodeWidth from 'scoreboard/components/node-width'

/**
 * Expands and collapses the object selector trees in order to show as much of the tree's
 * longest node's content. Issue #21253
 *
 * Example:
 * this.expandCollapse = new ExpandCollapse({
 * 	el: this.$el,
 * 	gardeners: [this.orgGardener, this.objectGardener],
 * 	types: ['org', 'object'],
 * 	unitType: '%'
 * 	hasContainer: false
 * }).render()
 *
 * @param {Object} el The element that contains the trees.
 * @param {Array} types The types of gardeners used.
 * @param {Object} gardeners The actual gardeners that will be used.
 * @param {String} unitType Whether the units of measurement should be pixel based or percentage.
 * @param {Boolean} hasContainer Optional. Pass `true ` if there is an outer container that can grow if needed. (i.e. Bootslide menus)
 *
 * The object selector's expanding/collapsing appears similar to that of the
 * tree-hover-expand-contract component but doesn't share a lot of code
 * for a few reasons (Issue #32402):
 *
 * 1. The nav pane only has a single tree so the math is different and only has
 * 		the option to use a single unit (px) while the object selectors can use
 * 		either px or %, depending on if it's within a bootslide menu or a dialog.
 * 2. The nav pane has extra things we need to check for to decide when
 * 		expanding/contracting is allowed. For instance, the manual drag handles and
 * 		edit mode drag and drop can both affect it.
 * 3. The nav pane also gets different classes applied/removed to it to make the
 * 		display-pane cooperate when it is expanding.
 */


var ObjectSelectorExpandCollapse = Backbone.View.extend({

	initialize: function(options) {
		this.options = options || {}
		_.each(this.options.gardeners, this.setTreeListeners.bind(this))
		_.each(this.options.types, this.setTreeNodeListeners.bind(this))
	},

	render: function(e) {
		if (this.options.gardeners.length === 2) {
			// If a new org is selected, a new object tree gets appended, so we
			// need to rebind all the listeners to the new tree.
			this.options.gardeners[0].on('select', () => {
				this.setTreeListeners(this.options.gardeners[1])
				this.setTreeNodeListeners(this.options.types[1])
			})
			this.$el.addClass('has-two-trees')
		}
		this.$el.toggleClass('has-percent-based-expanding', this.options.unitType === '%')
		return this
	},

	setTreeListeners(gardener) {
		gardener._onceTreeLoaded(() => {
			gardener.tree.on('rebind', () => {
				//wait for the transitions to finish so the nodes are in the DOM
				//and the animation doesn't stutter
				setTimeout(this.expandCollapse.bind(this), gardener.tree.transitionTimeout)
			})
		})
	},

	setTreeNodeListeners(type) {
		// If the user has long nodes in both trees, we want them to still be able to
		// see the long nodes in the tree they are looking at. So we will expand the hovered
		// tree and collapse the other one regardless of if the other one is overflowing.
		// Issue #32402
		if (this.options.unitType === '%') {
			let expandTimer
			// Check for expanding on mouseenter on the tree node and not the entire tree.
			const treeContainer = this.$(`.${type} .tree-container`)
			treeContainer.on('mouseenter', '.node' , event => {

				const expand = () => {
					// Only expand if a single node has been hovered for half a second without any mouse movement.
					expandTimer = setTimeout(() => {
						let hoveredNode = event.currentTarget
						const hoveredNodeEl = this.$(hoveredNode)

						// #57956 make sure the hovered node is still present.
						// In some circumstances, the node may be hovered over
						// right before navigation away occurs (i.e. a save
						// guard closes, the mouse hovers for a split second,
						// navigation occurs)
						if (!hoveredNodeEl.length) return

						let hoveredNodeWidth = nodeWidth(hoveredNodeEl)
						let hoveredTree = hoveredNode.closest('.org, .object')

						// If the width of the node is greater than its tree's width,
						// expand its tree and collapse the sibling tree.
						if (hoveredNodeWidth > this.$(hoveredTree).width()) {
							this.expandCollapse(hoveredTree)
						}
					}, constants.TREE_EXPAND_DELAY)
				}

				expand()

				this.$(event.currentTarget).on('mousemove', () => {
					clearTimeout(expandTimer)
					expand()
				})
				// If the mouse leaves the node before the setTimeout's function runs, cancel the expand.
				// Use `.one()` to prevent the mouseleave event from firing multiple
				// times when the mouse enters/leaves the same node multiple times
				.one('mouseleave click', e => {
					clearTimeout(expandTimer)
				})
			})

			// Every time the user mouses out of a tree we need to add a single
			// listener for the next time they leave.  MUST NOT listen to every
			// mouseleave event (#36100)
			const listenToTreeLostFocus = () => {
				// Check for collapsing on mouseleave on the entire object selector, so
				// it won't collapse if the user is working within the tree and wants to
				// do a search or click the expand all button
				treeContainer.one('mouseleave', (event) => {
					this.treeIsHovered = false
					this.expandCollapse()
					listenToTreeLostFocus()
				})
			}
			listenToTreeLostFocus()
		} else {
			// If its pixel based units, we don't need any of the mouseenter listeners
			// because the tree is within a tooltip and will expand as far as the
			// window allows if the content requires it.
			this.expandCollapse()
		}
	},

	expandCollapse(hoveredTree) {
		let types = this.options.types
		let treeEls = []
		let menuWidth = 0 // Starting point
		let minPixelWidth = this.options.types.length > 1 ? 300 : 600
		let currentWidth = this.$el.width() - constants.STANDARD_MARGIN*2 // 40 = padding
		let treeMinWidth = this.options.unitType === '%' ?
			currentWidth * .3 : minPixelWidth

		_.each(types, type => {
			let treeEl = this.$(`.${type}`)
			let tree = treeEl.find('.tree')[0]

			treeEl.addClass('is-getting-overflow-values')

			// If the tree hasn't been rendered yet, give it a placeholder min width
			let treeElScrollWidth = tree ? treeEl.find('ul')[0].scrollWidth : minPixelWidth

			let treeElWidth = treeElScrollWidth > treeMinWidth ?
				treeElScrollWidth : treeMinWidth
			if (hoveredTree === treeEl[0]) {
				treeEl.isHovered = true
			}
			treeEl.maxWidth = this.options.unitType === '%' ?
				_.round(treeElWidth / currentWidth * 100) :
					treeElWidth + constants.STANDARD_MARGIN

			// Limit the maxWidth of the tree to 70% so the sibling tree can take up
			// at least 30% of the viewable space.
			if (this.options.unitType === '%') {
				treeEl.maxWidth = Math.min(treeEl.maxWidth, 70)
			}
			treeEls.push(treeEl)
			treeEl.removeClass('is-getting-overflow-values')
		})

		// Max width adjustment for single tree menus
		if (treeEls.length === 1) {
			let singleTree = this.$(treeEls[0])
			// If the single tree is still loading, we need to wait until the `rebind`
			// listener triggers and confirms the tree is in the DOM
			if (singleTree.length > 0) {
				treeEls[0].maxWidth =
					Math.max(singleTree.width(), singleTree.find('ul')[0].scrollWidth)
			}
		}

		// #21617 for some reason, $('body').width() is sometimes returning 0
		// for Edge. getBoundingClientRect() seems to work better
		let maxWidth = this.options.unitType === '%' ? 100 :
			document.body.getBoundingClientRect().width - constants.STANDARD_MARGIN

		if (treeEls.length > 1) {
			let firstTreeElIsWider = treeEls[0].maxWidth > treeEls[1].maxWidth
			// Indexes of which tree is larger/smaller
			let largeTreeEl = firstTreeElIsWider ? 0 : 1
			let smallTreeEl = firstTreeElIsWider ? 1 : 0

			// If either the large or small trees are being hovered, check to see if
			// the hovered tree has overflowing content. If it does, we'll expand it up to 70%
			// and collapse the sibling to take up the remaining space. Both tree will adjust
			// back to their resting calculated widths on mouseleave.
			if (treeEls[largeTreeEl].isHovered &&
					this.isTreeOverflowing(treeEls[largeTreeEl])) {
				this.treeIsHovered = true
				treeEls[largeTreeEl].removeClass('is-not-truncating') // Class removed so text will fade out
				treeEls[smallTreeEl].maxWidth = maxWidth - treeEls[largeTreeEl].maxWidth
			} else if (treeEls[smallTreeEl].isHovered &&
					this.isTreeOverflowing(treeEls[smallTreeEl])) {
				this.treeIsHovered = true
				treeEls[smallTreeEl].removeClass('is-not-truncating') // Class removed so text will fade out
				treeEls[largeTreeEl].maxWidth = maxWidth - treeEls[smallTreeEl].maxWidth
			} else if (this.treeIsHovered) {
				// If the tree is expanded because the user hovered over it, it's already
				// at its max-width so we don't need to go further. The tree will collapse
				// on mouseleave.
				return
			} else if (treeEls[0].maxWidth + treeEls[1].maxWidth > maxWidth) {
				// If both treeEls widths are over half the width of the menu's allowed max width, set
				// both sides to be exactly half the maxed out menu.
				if (treeEls[0].maxWidth >= maxWidth/2 && treeEls[1].maxWidth >= maxWidth/2) {
					treeEls[0]
						.removeClass('is-not-truncating')
						.maxWidth = maxWidth/2
					treeEls[1]
						.removeClass('is-not-truncating')
						.maxWidth = maxWidth/2
				} else {
					// Otherwise, the narrower treeEl isn't overflowing so we can give the narrow one its natural
					// width, and have the wider one take up the remaining space allowed.
					treeEls[largeTreeEl]
						.removeClass('is-not-truncating') // Class removed so text will fade out
						.maxWidth = maxWidth - treeEls[smallTreeEl].maxWidth
					treeEls[smallTreeEl].addClass('is-not-truncating') // Class removed so text will fade out
				}
			} else {
				// Otherwise, we know nothing needs truncated so we don't need to fade anything out
				treeEls[0].addClass('is-not-truncating')
				treeEls[1].addClass('is-not-truncating')

				// If percentage based units, the container doesn't expand/collapse so the trees
				// need to fill the entire space available.
				if (this.options.unitType === '%') {
					// If the large tree is over half the width of the container, but the second tree doesn't overflow
					// the container, have the small container take up the remaining space.
					if (treeEls[largeTreeEl].maxWidth > 50) {
						treeEls[smallTreeEl].maxWidth = maxWidth - treeEls[largeTreeEl].maxWidth
					} else {
						// Otherwise, if both are under half the size of the container, make them both take up equal space.
						treeEls[0].maxWidth = 50
						treeEls[1].maxWidth = 50
					}
				}
			}
		} else {
			// If only one tree:
			// if the tree doesn't need to truncate because its narrower than the allowed max-width of menu,
			// remove the label-mask's fade.
			if (treeEls[0].maxWidth < maxWidth) {
				treeEls[0].addClass('is-not-truncating')
			} else {
				// Otherwise, set max width to menu's allowed max width add the fade.
				treeEls[0]
					.removeClass('is-not-truncating')
					.maxWidth = maxWidth
			}
		}

		// Now that all of the maxWidths have been determined, we can apply them
		treeEls.forEach((treeEl) => {
			treeEl
				.removeClass('is-getting-overflow-values')
				.css('width', `${treeEl.maxWidth}${this.options.unitType}`)
			menuWidth += treeEl.maxWidth
		})

		if (this.options.hasContainer) {
			process.nextTick(() => this.trigger('updateWidth', menuWidth))
		}
	},

	isTreeOverflowing: function (treeEl) {
		let list = treeEl.find('ul')[0]
		if (list) {
			return _.round(treeEl.width()) < list.scrollWidth
		}
	}

})

export default ObjectSelectorExpandCollapse

ObjectSelectorExpandCollapse.__filename__ = 'components/object-selector-expand-collapse.js'
